import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetOrderById } from "@/services";
import {
	ErrorBoundary,
	Icon,
	TabItem,
	Tabs,
	TabsList,
} from "@st-marketplace/ui";
import { type FC, useState } from "react";
import {
	OrderDetailsHeader,
	OrderDetailsHistory,
	OrderDetailsInfo,
	OrderDetailsInfoSkeleton,
	OrderDetailsTransactions,
} from "./components";

interface OrderDetailsProps {
	orderId: string;
}

const OrderDetails: FC<OrderDetailsProps> = ({ orderId }) => {
	const [tabValue, setTabValue] = useState("0");
	const { getErrorInterface } = useApiError();

	const { t } = useTranslation();

	const {
		data: orderData,
		isLoading,
		error,
		refetch,
	} = useGetOrderById({
		path: { order_id: orderId },
	});

	// a customer-link order not claimed yet has no EA account, so nothing was delivered yet
	const isUnclaimed = orderData?.schema_type === "end_customer_unclaimed";
	const showTransactions =
		orderData?.schema_type !== "for_buyer" && !isUnclaimed;

	return (
		<div className="flex flex-col gap-6">
			<ErrorBoundary
				isLoading={isLoading}
				error={error ? getErrorInterface(error) : undefined}
				onReset={refetch}
				skeleton={<OrderDetailsInfoSkeleton />}
			>
				{orderData ? (
					<>
						<OrderDetailsHeader order={orderData} />
						<Tabs value={tabValue} onValueChange={setTabValue}>
							<TabsList>
								<TabItem value="0" Icon={<Icon name="information-line" />}>
									{t("orderInfo.tabInfo")}
								</TabItem>
								{showTransactions && (
									<TabItem value="1" Icon={<Icon name="exchange-line" />}>
										{t("orderInfo.tabTransactions")}
									</TabItem>
								)}
								{!isUnclaimed && (
									<TabItem value="2" Icon={<Icon name="file-list-3-line" />}>
										{t("orderInfo.tabHistory")}
									</TabItem>
								)}
							</TabsList>
						</Tabs>
						{tabValue === "0" && <OrderDetailsInfo data={orderData} />}
						{tabValue === "1" && showTransactions && (
							<OrderDetailsTransactions />
						)}
						{tabValue === "2" && "origin_instance_id" in orderData && (
							<OrderDetailsHistory instanceId={orderData.origin_instance_id} />
						)}
					</>
				) : null}
			</ErrorBoundary>
		</div>
	);
};

export default OrderDetails;
